import * as ActionTypes from '@/stores/sale/actions';
import uuid from 'uuid/v4';
import _ from 'lodash';
import message from '@/utils/message';
import perms from '@/common/permissions/sale/perms';
import dialog from '@/utils/dialog';
import action from '@/pages/sysadmin/callcenterConfig/api/index.js';
import commonReport from '@/common/common-report';

/**
 * 呼叫中心服务
 */
class CallCenter {
  /**
   * 构造函数
   * @param webApi WebApi
   * @param messageBox 消息对话框
   * @param vendor 呼叫中心供应商，如青牛的 `channelsoft/ChannelSoftCC`
   */
  constructor(webApi, vendor) {
    this.webApi = webApi;
    this.vendor = vendor;
  }

  /**
   * 初始化呼叫中心，同时绑定用户登录态
   * @param loginStatus 当前用户登录态
   */
  init(loginStatus) {
    this.loginStatus = loginStatus;
    this.vendor.bind(loginStatus);
    this.vendor.onDialOut(this._handleDialOut);
    this.vendor.onDialStatusUpdate(this._handleDialStatusUpdate);
  }

  /**
   * 当呼出时，触发回调
   * @param callback
   */
  onDialOut(callback) {
    this.dialOutCallback = callback;
  }

  /**
   * 当状态发生改变时，触发回调，告诉外层是否可以呼出电话
   * @param callback
   */
  onUpdateStatus(callback) {
    // 登录成功 && 登出成功
    return this.vendor.onUpdateStatus(callback);
  }

  /**
   * 自动弹出拨号面板
   */
  autoShowPanel() {
    return this.vendor.autoShowPanel();
  }

  /**
   * 登录
   */
  login() {
    return this.vendor.login();
  }

  /**
   * @description 呼叫指定的电话号码
   * @param leadId
   * @param contactId
   * @param number
   * @return {Promise<any>}
   */
  dialOut({ leadId, contactId, number, leadDetail }) {
    return this.vendor.dialOut(number, { leadId, contactId, leadDetail });
  }

  /**
   * 处理呼出请求
   * @param params
   * @returns {Promise<{autoDialOut: boolean}>}
   */
  _handleDialOut = ({ mode, isIntellectCall, callerNumber, calleeNumber, makeCall, leadDetail, ...more }) => {
    const params = {
      mode,
      isIntellectCall,
      contactPhone: calleeNumber?.replaceAll?.(' ', ''),
      displayNumber: callerNumber,
      vendor: this.vendor.name,
      uuid: uuid(), // 前端生成的唯一ID
      ...more,
    };
    return this.webApi
      .dialOutCheck()
      .ajaxJson({
        data: params,
      })
      .then((resp) => {
        const dialOut = (leadId) => {
          const beginCall = () => {
            this.webApi
              .dialOut()
              .dispatchJson(ActionTypes.DIAL_OUT, {
                data: leadId ? _.assign({}, params, { leadId }) : params,
              })
              .then((r2) => {
                makeCall(r2.phoneNumber, params, r2, { leadId });
                return r2;
              });
          };
          let lineId = '';
          const lineList = qd.getState('switchboardList'); // 所有线路
          const isSystemDistribute = this.vendor.currentNumber === '系统匹配';
          if (!isSystemDistribute) {
            lineList.forEach((item) => {
              if (item.switchboard_number === this.vendor.currentNumber) {
                lineId = item.switchboard_line_id;
              }
            });
          }
          // number 和线路 id 匹配
          action.checkLineMatch({
            // isSystemDistribute, // 是否系统匹配
            leadId: leadId ? leadId : more.leadId, // 线索 id
            lineId, // 线路 id
          }).then((res) => {
            // 当服务器正常 code === 0；切 match 时可拨打电话 || 服务挂了 可拨打电话 || 服务素的上游服务挂了，也可拨打电话
            if (res.code === 0) {
              if (res.data.isPass) {
                beginCall();
              } else {
                const numberList = [];
                const {
                  availableLineIdList,
                } = res.data;
                lineList.forEach((item) => {
                  if (availableLineIdList.includes(+item.switchboard_line_id) || availableLineIdList.includes(`${item.switchboard_line_id}`)) {
                    numberList.push(item.switchboard_number.replace('+86', '').padStart(11, 0));
                  }
                });

                dialog.confirm(<div>
                  <div>当前所选号码不符合该类线索呼叫要求，请在呼叫面板中选择其他号码</div>
                  {numberList.length > 0
                    && <div>可选号码为：{numberList.join(',')}</div>
                  }
                </div>);
              }
            } else {
              beginCall();
            }
          })
            .catch(() => {
              beginCall();
            });
          /**
          return this.webApi
            .dialOut()
            .dispatchJson(ActionTypes.DIAL_OUT, {
              data: leadId ? _.assign({}, params, { leadId: leadId }) : params,
            })
            .then((r2) => {
              makeCall(r2.phoneNumber, params, r2, { leadId })
              return r2
            })
           **/
        };

        // 渲染界面
        if (this.dialOutCallback) {
          this.dialOutCallback(params, resp, dialOut, leadDetail);
        }

        // 外呼限制必须关联线索权限判断
        const grayPermWithLeads = _.includes(this.loginStatus?.grayPermissions, perms.GRAY_CALL_CENTER_DIAL_OUT_RELATION_LEAD);

        if (grayPermWithLeads && !resp.mobileLead) {
          return resp;
        }

        // 返回结果, 用户点击呼叫链接时直接拨打
        if (_.isEmpty(resp.leadList) && resp.status === 'PHONE_STATUS_WHITE' && resp.autoDialOut) {
          return dialOut();
        }
        return resp;
      })
      .catch((error) => {
        message.error(`外呼错误 ${error.message}`);
      });
  }

  _handleDialStatusUpdate = (dialStatus) => {
    const traceInfo = {
      ...dialStatus.params,
      sessionId: dialStatus.sessionId,
      dialDirection: dialStatus.dialDirection,
      callingStatus: dialStatus.callingStatus,
      actionRecordList: dialStatus.resp?.actionRecordList || [],
    };

    delete traceInfo.leadDetail;

    if (traceInfo.leadId) {
      this.webApi
        .traceCalling()
        .ajaxJson({
          data: traceInfo,
        })
        .catch((err) => {
          // 捕获上报异常
          // commonReport.reportError({
          //   message: `TraceInfo: ${traceInfo}; DialStatus: ${dialStatus}; TraceCallingError ${err.message}`,
          //   trace: traceInfo.sessionId,
          // });
        });
    }
  }

  /**
   * @description: 电话呼入
   */
  onDialIn = (callback) => {
    return this.vendor.onDialIn(callback);
  }

  handleDialIn = () => {
    return this.vendor.dialIn();
  }
}

export default CallCenter;
